/**
 * EAAnalysisResults Component
 * Displays Q.AI analysis results with the new async response format
 *
 * New Response Structure (from DataFeed):
 * - summary: string
 * - lineCount: number
 * - fileSizeKB: number
 * - bugs[]: { line, severity, category, description, codeSnippet, suggestedFix }
 * - enhancements[]: { type, description, complexity, estimatedHours }
 * - quality: { readability, maintainability, errorHandling, riskManagement }
 * - structure: { entryLogic, exitLogic, riskManagement, indicators, functions[] }
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Grid,
  Collapse,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CodeIcon from '@mui/icons-material/Code';
import ChatIcon from '@mui/icons-material/Chat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BoltIcon from '@mui/icons-material/Bolt';
import ShieldIcon from '@mui/icons-material/Shield';
import SpeedIcon from '@mui/icons-material/Speed';
import BuildIcon from '@mui/icons-material/Build';
import TimelineIcon from '@mui/icons-material/Timeline';
import FunctionsIcon from '@mui/icons-material/Functions';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getFileTypeConfig } from '../../../api/qbridgeEaApi';

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

const getSeverityColor = (severity) => {
  const colors = {
    critical: '#EF4444',
    high: '#F97316',
    medium: '#FBBF24',
    low: '#A3A3A3',
    info: '#60A5FA'
  };
  return colors[severity?.toLowerCase()] || colors.medium;
};

const getComplexityColor = (complexity) => {
  const colors = {
    low: '#22C55E',
    medium: '#FBBF24',
    high: '#F97316'
  };
  return colors[complexity?.toLowerCase()] || colors.medium;
};

const getScoreColor = (score) => {
  if (score >= 8) return '#22C55E';
  if (score >= 6) return '#FBBF24';
  if (score >= 4) return '#F97316';
  return '#EF4444';
};

/**
 * Quality Score Card
 */
const ScoreCard = ({ label, score, icon: Icon }) => {
  // Safely handle null, undefined, NaN, and non-numeric values
  const safeScore = (typeof score === 'number' && !isNaN(score)) ? score : 0;
  return (
    <Paper
      sx={{
        p: 2,
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '10px',
        textAlign: 'center'
      }}
    >
      <Icon sx={{ color: getScoreColor(safeScore), fontSize: '1.5rem', mb: 1 }} />
      <Typography sx={{ color: getScoreColor(safeScore), fontSize: '1.5rem', fontWeight: 700 }}>
        {safeScore.toFixed(1)}
      </Typography>
      <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.75rem' }}>
        {label}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={safeScore * 10}
        sx={{
          mt: 1,
          height: 4,
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: getScoreColor(safeScore),
            borderRadius: 2
          }
        }}
      />
    </Paper>
  );
};

/**
 * Risk Level Badge
 */
const RiskLevelBadge = ({ level }) => {
  const config = {
    low: { color: '#22C55E', bg: 'rgba(34, 197, 94, 0.15)', label: 'Low Risk' },
    medium: { color: '#FBBF24', bg: 'rgba(251, 191, 36, 0.15)', label: 'Medium Risk' },
    high: { color: '#F97316', bg: 'rgba(249, 115, 22, 0.15)', label: 'High Risk' },
    critical: { color: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)', label: 'Critical' },
    unknown: { color: '#71717A', bg: 'rgba(113, 113, 122, 0.15)', label: 'Unknown' }
  };

  const { color, bg, label } = config[level] || config.unknown;

  return (
    <Chip
      icon={<ShieldIcon sx={{ fontSize: '0.875rem', color: `${color} !important` }} />}
      label={label}
      size="small"
      sx={{
        backgroundColor: bg,
        color: color,
        fontWeight: 600,
        fontSize: '0.75rem',
        height: '26px',
        border: `1px solid ${color}30`,
        '& .MuiChip-icon': { ml: 0.5 }
      }}
    />
  );
};

/**
 * Bug Item Component - Enhanced with expandable details
 */
const BugItem = ({ bug }) => {
  const [expanded, setExpanded] = useState(false);
  const severityColor = getSeverityColor(bug.severity);

  return (
    <Paper
      sx={{
        p: 2,
        mb: 1.5,
        background: `${severityColor}08`,
        border: `1px solid ${severityColor}25`,
        borderLeft: `3px solid ${severityColor}`,
        borderRadius: '8px',
        cursor: bug.codeSnippet || bug.suggestedFix ? 'pointer' : 'default'
      }}
      onClick={() => (bug.codeSnippet || bug.suggestedFix) && setExpanded(!expanded)}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <ErrorOutlineIcon sx={{ color: severityColor, fontSize: '1.125rem', mt: 0.25 }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={bug.severity || 'medium'}
              size="small"
              sx={{
                backgroundColor: `${severityColor}20`,
                color: severityColor,
                fontWeight: 600,
                fontSize: '0.625rem',
                height: '20px',
                textTransform: 'uppercase'
              }}
            />
            {bug.category && (
              <Chip
                label={bug.category}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.625rem',
                  height: '20px'
                }}
              />
            )}
            {bug.line && (
              <Chip
                label={`Line ${bug.line}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(124, 58, 237, 0.15)',
                  color: '#a855f7',
                  fontWeight: 600,
                  fontSize: '0.625rem',
                  height: '20px',
                  fontFamily: "'JetBrains Mono', monospace"
                }}
              />
            )}
          </Box>
          <Typography sx={{ color: '#FFFFFF', fontSize: '0.875rem', lineHeight: 1.5 }}>
            {bug.description}
          </Typography>
        </Box>
        {(bug.codeSnippet || bug.suggestedFix) && (
          <Box sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        )}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2, pl: 4 }}>
          {bug.codeSnippet && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem', mb: 0.5 }}>
                Code:
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '6px',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.75rem',
                  color: 'rgba(255, 255, 255, 0.8)',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {bug.codeSnippet}
              </Box>
            </Box>
          )}
          {bug.suggestedFix && (
            <Box
              sx={{
                p: 1.5,
                backgroundColor: 'rgba(39, 181, 109, 0.1)',
                borderRadius: '6px',
                border: '1px solid rgba(39, 181, 109, 0.2)'
              }}
            >
              <Typography sx={{ color: '#27B56D', fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>
                Suggested Fix:
              </Typography>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8125rem' }}>
                {bug.suggestedFix}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

/**
 * Enhancement Item Component - Updated for new format
 */
const EnhancementItem = ({ enhancement }) => {
  const complexityColor = getComplexityColor(enhancement.complexity);

  return (
    <Paper
      sx={{
        p: 2,
        mb: 1.5,
        background: 'rgba(124, 58, 237, 0.08)',
        border: '1px solid rgba(124, 58, 237, 0.2)',
        borderRadius: '8px'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <LightbulbOutlinedIcon sx={{ color: '#a855f7', fontSize: '1.125rem', mt: 0.25 }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            {enhancement.type && (
              <Chip
                label={enhancement.type}
                size="small"
                sx={{
                  backgroundColor: 'rgba(124, 58, 237, 0.2)',
                  color: '#a855f7',
                  fontWeight: 600,
                  fontSize: '0.625rem',
                  height: '20px'
                }}
              />
            )}
            {enhancement.complexity && (
              <Chip
                label={`${enhancement.complexity} complexity`}
                size="small"
                sx={{
                  backgroundColor: `${complexityColor}20`,
                  color: complexityColor,
                  fontSize: '0.625rem',
                  height: '20px'
                }}
              />
            )}
            {enhancement.estimatedHours && (
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
                ~{enhancement.estimatedHours}h effort
              </Typography>
            )}
          </Box>
          <Typography sx={{ color: '#FFFFFF', fontSize: '0.875rem', lineHeight: 1.5 }}>
            {typeof enhancement === 'string' ? enhancement : enhancement.description}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

/**
 * EAAnalysisResults Component
 * @param {object} file - The analyzed file
 * @param {object} analysis - Analysis results from async API
 * @param {number} tokensUsed - Tokens consumed by analysis
 * @param {number} processingTimeMs - Processing time in milliseconds
 * @param {number} balanceRemaining - Remaining token balance
 * @param {function} onViewChanges - Handler to view code diff
 * @param {function} onAskQuestion - Handler to ask Q.AI about the EA
 * @param {function} onNewAnalysis - Handler to start a new analysis (reset)
 * @param {boolean} hasModifications - Whether there are code modifications available
 */
const EAAnalysisResults = ({
  file = null,
  analysis = null,
  tokensUsed = 0,
  processingTimeMs = 0,
  balanceRemaining = 0,
  onViewChanges,
  onAskQuestion,
  onNewAnalysis,
  hasModifications = false
}) => {
  const [showAllBugs, setShowAllBugs] = useState(false);
  const [showAllEnhancements, setShowAllEnhancements] = useState(false);
  const [showStructure, setShowStructure] = useState(false);

  if (!file || !analysis) {
    return null;
  }

  const fileConfig = getFileTypeConfig(file.file_type);

  // DEBUG: Log raw analysis data to diagnose structure
  console.log('[EAAnalysisResults] Raw analysis object:', JSON.stringify(analysis, null, 2));
  console.log('[EAAnalysisResults] Analysis keys:', Object.keys(analysis || {}));

  // Extract data from new response format (with fallbacks to old format)
  // Note: Backend may return PascalCase (from C#) or camelCase - handle both
  const bugs = analysis.bugs || analysis.Bugs || analysis.issues || analysis.Issues || [];
  const enhancements = analysis.enhancements || analysis.Enhancements || analysis.suggestions || analysis.Suggestions || [];
  const rawQuality = analysis.quality || analysis.Quality || {};
  const structure = analysis.structure || analysis.Structure || {};
  const summary = analysis.summary || analysis.Summary || '';

  // DEBUG: Log extracted quality data
  console.log('[EAAnalysisResults] Raw quality:', JSON.stringify(rawQuality, null, 2));

  // Normalize quality metrics - backend returns camelCase with "Score" suffix
  // e.g., readabilityScore, maintainabilityScore, errorHandlingScore, riskManagementScore
  const quality = {
    readability: rawQuality.readabilityScore ?? rawQuality.ReadabilityScore ?? rawQuality.readability ?? rawQuality.Readability ?? 0,
    maintainability: rawQuality.maintainabilityScore ?? rawQuality.MaintainabilityScore ?? rawQuality.maintainability ?? rawQuality.Maintainability ?? 0,
    errorHandling: rawQuality.errorHandlingScore ?? rawQuality.ErrorHandlingScore ?? rawQuality.errorHandling ?? rawQuality.ErrorHandling ?? 0,
    riskManagement: rawQuality.riskManagementScore ?? rawQuality.RiskManagementScore ?? rawQuality.riskManagement ?? rawQuality.RiskManagement ?? 0,
    overallScore: rawQuality.overallScore ?? rawQuality.OverallScore ?? 0,
    complexity: rawQuality.complexity ?? rawQuality.Complexity ?? 'unknown'
  };

  const visibleBugs = showAllBugs ? bugs : bugs.slice(0, 3);
  const visibleEnhancements = showAllEnhancements ? enhancements : enhancements.slice(0, 3);

  // Count bugs by severity
  const bugsBySeverity = bugs.reduce((acc, bug) => {
    const severity = (bug.severity || 'medium').toLowerCase();
    acc[severity] = (acc[severity] || 0) + 1;
    return acc;
  }, {});

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#0F0F14',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        overflow: 'hidden'
      }}
    >
      {/* ════════════════════════════════════════════════════════════════════════
          HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          backgroundColor: 'rgba(255, 255, 255, 0.02)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CheckCircleIcon sx={{ color: '#27B56D', fontSize: '1.25rem' }} />
            <Typography sx={{ color: '#FFFFFF', fontSize: '1rem', fontWeight: 600 }}>
              Analysis Complete
            </Typography>
          </Box>
          {/* Bug Summary Chips */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {bugsBySeverity.critical > 0 && (
              <Chip label={`${bugsBySeverity.critical} Critical`} size="small"
                sx={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#EF4444', fontSize: '0.625rem', height: '22px' }} />
            )}
            {bugsBySeverity.high > 0 && (
              <Chip label={`${bugsBySeverity.high} High`} size="small"
                sx={{ backgroundColor: 'rgba(249, 115, 22, 0.15)', color: '#F97316', fontSize: '0.625rem', height: '22px' }} />
            )}
          </Box>
        </Box>

        {/* File Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              backgroundColor: `${fileConfig.color}20`,
              color: fileConfig.color,
              fontWeight: 700,
              fontSize: '0.625rem',
              padding: '3px 6px',
              borderRadius: '4px',
              border: `1px solid ${fileConfig.color}40`
            }}
          >
            {fileConfig.label}
          </Box>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.8125rem',
              fontFamily: "'JetBrains Mono', monospace"
            }}
          >
            {file.name}
          </Typography>
        </Box>

        {/* Tokens & Time */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BoltIcon sx={{ color: '#a855f7', fontSize: '0.875rem' }} />
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
              {tokensUsed} tokens used
            </Typography>
          </Box>
          {(typeof processingTimeMs === 'number' && processingTimeMs > 0) && (
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.75rem' }}>
              {(processingTimeMs / 1000).toFixed(1)}s
            </Typography>
          )}
        </Box>
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          CONTENT - Scrollable
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '3px'
          }
        }}
      >
        {/* Summary */}
        {summary && (
          <Paper
            sx={{
              p: 2.5,
              mb: 3,
              background: 'rgba(39, 181, 109, 0.08)',
              border: '1px solid rgba(39, 181, 109, 0.2)',
              borderRadius: '12px'
            }}
          >
            <Typography
              sx={{
                color: '#27B56D',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 1
              }}
            >
              Summary
            </Typography>
            <Typography sx={{ color: '#FFFFFF', fontSize: '0.9375rem', lineHeight: 1.6 }}>
              {summary}
            </Typography>
          </Paper>
        )}

        {/* Quality Metrics */}
        {Object.keys(quality).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                color: '#FFFFFF',
                fontSize: '0.875rem',
                fontWeight: 600,
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <SpeedIcon sx={{ fontSize: '1rem', color: '#60A5FA' }} />
              Quality Metrics
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={3}>
                <ScoreCard label="Readability" score={quality.readability} icon={CodeIcon} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <ScoreCard label="Maintainability" score={quality.maintainability} icon={BuildIcon} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <ScoreCard label="Error Handling" score={quality.errorHandling} icon={ShieldIcon} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <ScoreCard label="Risk Mgmt" score={quality.riskManagement} icon={TimelineIcon} />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Bugs Section */}
        {bugs.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberIcon sx={{ color: '#FBBF24', fontSize: '1rem' }} />
                <Typography sx={{ color: '#FFFFFF', fontSize: '0.875rem', fontWeight: 600 }}>
                  Bugs & Issues
                </Typography>
                <Chip
                  label={bugs.length}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(251, 191, 36, 0.15)',
                    color: '#FBBF24',
                    fontWeight: 700,
                    fontSize: '0.6875rem',
                    height: '20px'
                  }}
                />
              </Box>
            </Box>

            {visibleBugs.map((bug, index) => (
              <BugItem key={index} bug={bug} />
            ))}

            {bugs.length > 3 && (
              <Button
                onClick={() => setShowAllBugs(!showAllBugs)}
                startIcon={showAllBugs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.8125rem',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
                }}
              >
                {showAllBugs ? 'Show less' : `Show ${bugs.length - 3} more`}
              </Button>
            )}
          </Box>
        )}

        {/* No Bugs Message */}
        {bugs.length === 0 && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              background: 'rgba(39, 181, 109, 0.08)',
              border: '1px solid rgba(39, 181, 109, 0.2)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <CheckCircleIcon sx={{ color: '#27B56D', fontSize: '1.25rem' }} />
            <Typography sx={{ color: '#27B56D', fontSize: '0.875rem', fontWeight: 500 }}>
              No critical bugs found in your code!
            </Typography>
          </Paper>
        )}

        {/* Enhancements Section */}
        {enhancements.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <LightbulbOutlinedIcon sx={{ color: '#a855f7', fontSize: '1rem' }} />
              <Typography sx={{ color: '#FFFFFF', fontSize: '0.875rem', fontWeight: 600 }}>
                Suggested Enhancements
              </Typography>
              <Chip
                label={enhancements.length}
                size="small"
                sx={{
                  backgroundColor: 'rgba(124, 58, 237, 0.15)',
                  color: '#a855f7',
                  fontWeight: 700,
                  fontSize: '0.6875rem',
                  height: '20px'
                }}
              />
            </Box>

            {visibleEnhancements.map((enhancement, index) => (
              <EnhancementItem key={index} enhancement={enhancement} />
            ))}

            {enhancements.length > 3 && (
              <Button
                onClick={() => setShowAllEnhancements(!showAllEnhancements)}
                startIcon={showAllEnhancements ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.8125rem',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
                }}
              >
                {showAllEnhancements ? 'Show less' : `Show ${enhancements.length - 3} more`}
              </Button>
            )}
          </Box>
        )}

        {/* Structure Section */}
        {structure && (structure.entryLogic || structure.functions?.length > 0) && (
          <Box sx={{ mb: 3 }}>
            <Button
              onClick={() => setShowStructure(!showStructure)}
              startIcon={<FunctionsIcon />}
              endIcon={showStructure ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                color: '#60A5FA',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                mb: 1,
                '&:hover': { backgroundColor: 'rgba(96, 165, 250, 0.1)' }
              }}
            >
              Code Structure
            </Button>

            <Collapse in={showStructure}>
              <Paper
                sx={{
                  p: 2,
                  background: 'rgba(96, 165, 250, 0.08)',
                  border: '1px solid rgba(96, 165, 250, 0.2)',
                  borderRadius: '10px'
                }}
              >
                {structure.entryLogic && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ color: '#60A5FA', fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>
                      Entry Logic:
                    </Typography>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8125rem' }}>
                      {structure.entryLogic}
                    </Typography>
                  </Box>
                )}

                {structure.exitLogic && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ color: '#60A5FA', fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>
                      Exit Logic:
                    </Typography>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8125rem' }}>
                      {structure.exitLogic}
                    </Typography>
                  </Box>
                )}

                {structure.indicators?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ color: '#60A5FA', fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>
                      Indicators Used:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {structure.indicators.map((indicator, i) => (
                        <Chip
                          key={i}
                          label={indicator}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(96, 165, 250, 0.2)',
                            color: '#60A5FA',
                            fontSize: '0.6875rem'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {structure.functions?.length > 0 && (
                  <Box>
                    <Typography sx={{ color: '#60A5FA', fontSize: '0.75rem', fontWeight: 600, mb: 1 }}>
                      Functions ({structure.functions.length}):
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.5)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', fontSize: '0.6875rem' }}>Name</TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.5)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', fontSize: '0.6875rem' }}>Type</TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.5)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', fontSize: '0.6875rem' }}>Lines</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {structure.functions.slice(0, 10).map((func, i) => (
                            <TableRow key={i}>
                              <TableCell sx={{ color: '#FFFFFF', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}>
                                {func.name}
                              </TableCell>
                              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.6)', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', fontSize: '0.75rem' }}>
                                {func.type || '-'}
                              </TableCell>
                              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.6)', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', fontSize: '0.75rem' }}>
                                {func.lines || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Paper>
            </Collapse>
          </Box>
        )}
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          ACTION BUTTONS
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          padding: '16px 20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          display: 'flex',
          gap: 1.5
        }}
      >
        {/* View Code Changes - Primary CTA */}
        {hasModifications && (
          <Button
            onClick={onViewChanges}
            startIcon={<CodeIcon />}
            sx={{
              flex: 1,
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              padding: '12px 20px',
              borderRadius: '10px',
              '&:hover': {
                background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)'
              }
            }}
          >
            View Code Changes
          </Button>
        )}

        {/* Ask Q.AI - Secondary */}
        <Button
          onClick={onAskQuestion}
          startIcon={<ChatIcon />}
          sx={{
            flex: hasModifications ? 0 : 1,
            minWidth: hasModifications ? '140px' : undefined,
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 500,
            padding: '12px 20px',
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderColor: 'rgba(255, 255, 255, 0.2)'
            }
          }}
        >
          Ask Q.AI
        </Button>

        {/* Re-analyze - Tertiary */}
        {onNewAnalysis && (
          <Button
            onClick={onNewAnalysis}
            startIcon={<RefreshIcon />}
            sx={{
              minWidth: '44px',
              padding: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              color: 'rgba(255, 255, 255, 0.6)',
              textTransform: 'none',
              borderRadius: '10px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderColor: 'rgba(255, 255, 255, 0.15)',
                color: '#FFFFFF'
              }
            }}
            title="Run new analysis"
          />
        )}
      </Box>
    </Box>
  );
};

export default EAAnalysisResults;
